/**
 * `MissionRow` — one mission as a top-level row in the Team of
 * Agents pane.
 *
 * Layout (2026-07-02 pane redesign, operator brief): the state is
 * carried by the GLYPH ALONE (animated spinner while active; the old
 * state word was redundant) — the localized state label lives on the
 * row tooltip + aria-label. Two content lines:
 *
 *   1. goal — clamped to 2 lines (the goal IS the content; hard
 *      truncation hid it) + queue-position chip
 *   2. meta — leaf progress ("2/5 etapas") · clock stamp · elapsed
 *      (ticks live while active) in thin small type. Special
 *      subtitles (awaiting-user timeout) keep their own amber line.
 *
 * Also renders: cancel icon-button (§I.13; native `confirm()`),
 * expand chevron when `leaves.length > 0` (auto-expanded for
 * `awaiting_user` per plan §D.1), child `MissionLeafRow`s on expand.
 */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Check,
  ChevronDown,
  ChevronRight,
  Circle,
  Clock,
  Loader2,
  Pause,
  Trash2,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useMissionsStore } from "@/stores/useMissionsStore";
import { useUIStore } from "@/stores/useUIStore";
import { formatClock, formatElapsed, joinMeta, useNow } from "./row-time";
import { isActiveMissionState } from "./monitor-tree";
import { ScheduledBadge } from "./ScheduledBadge";
import { CaseChip } from "./CaseChip";
import { StatusBullet } from "./StatusBullet";
import type { MissionLeafNode, MissionTreeNode } from "./monitor-tree";
import type { MissionState, NodeStatus } from "@/api/lx-mission";
import { normalizeProgressPct } from "@/lib/agents/progress-pct";

export interface MissionRowProps {
  node: MissionTreeNode;
  onCancel: (missionId: string) => Promise<void> | void;
  /** Resolved case title for `node.caseId`, when the registry knows it. */
  caseLabel?: string;
  /** The case currently on screen — drives the chip's emphasis. */
  activeCaseId?: string | null;
  /** False until the case registry has loaded — gates the unresolved
   *  diagnostic so it cannot fire on first paint. */
  caseRegistryLoaded?: boolean;
}

export function MissionRow({
  node,
  onCancel,
  caseLabel,
  activeCaseId,
  caseRegistryLoaded,
}: MissionRowProps) {
  const { t, i18n } = useTranslation();
  const focusedRowId = useMissionsStore((s) => s.focusedRowId);
  const openMissionDetail = useUIStore((s) => s.openMissionDetail);
  const focused = focusedRowId === `mission:${node.missionId}`;
  const rowRef = useRef<HTMLLIElement | null>(null);
  useEffect(() => {
    if (focused && rowRef.current) {
      rowRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [focused]);
  // Auto-expand rule (sidebar redesign 2026-07-19): a run the lawyer can
  // still act on shows its steps WITHOUT a click — awaiting-user (which
  // leaf is blocked?) and anything still in flight (what is it doing?).
  // Terminal runs collapse to their header and stay expandable on demand.
  const [expanded, setExpanded] = useState<boolean>(
    node.awaitingUser || isActiveMissionState(node.state),
  );
  const hasLeaves = node.leaves.length > 0;

  // §K-bis: body click opens the detail dialog; the chevron (when
  // present) toggles inline-leaf expansion via stopPropagation.
  const openDetail = () => openMissionDetail(node.missionId);
  const toggleExpanded = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    if (hasLeaves) setExpanded((v) => !v);
  };

  const isTerminal =
    node.state === "completed" ||
    node.state === "failed" ||
    node.state === "canceled";
  const isActive = !isTerminal && node.state !== "queued";
  // Queued rows deliberately carry NO inline cancel: the queue is a calm
  // waiting list, and a destructive control on every row invites the
  // accidental click. Cancelling a gated mission goes through the detail
  // dialog (`mission-detail-cancel`), which the row body opens — same
  // handler, same confirm, one deliberate step further away.
  const showCancel =
    !isTerminal && !node.isCanceling && node.state !== "queued";

  const handleCancel = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const confirmed = window.confirm(
      t("rightSidebar.confirmCancel", { goal: node.goal }),
    );
    if (!confirmed) return;
    await onCancel(node.missionId);
  };

  // The state WORD lives on tooltip + aria only (glyph carries it).
  const stateLabel = node.isCanceling
    ? t("rightSidebar.canceling")
    : t(`rightSidebar.missionState.${node.state}`);

  // Live elapsed for active missions; frozen duration once terminal.
  const now = useNow(isActive || node.isCanceling);
  const elapsedMs = isTerminal
    ? node.completedAt !== undefined
      ? node.completedAt - node.openedAt
      : undefined
    : now - node.openedAt;

  const totalLeaves = node.leaves.length;
  const doneLeaves = node.leaves.filter((l) => l.status === "completed").length;
  const meta = joinMeta(
    totalLeaves > 0
      ? t("rightSidebar.leavesProgress", { done: doneLeaves, total: totalLeaves })
      : undefined,
    formatClock(node.openedAt, i18n.language),
    elapsedMs !== undefined ? formatElapsed(elapsedMs) : undefined,
  );

  return (
    <li
      ref={rowRef}
      data-testid={`mission-row-${node.missionId}`}
      data-state={node.state}
      data-focused={focused ? "true" : undefined}
      className={cn(
        "border-b border-cream-200/60 last:border-b-0 transition-colors dark:border-coffee-800/60",
        focused && "bg-blue-50 dark:bg-blue-950/30",
      )}
    >
      <button
        type="button"
        onClick={openDetail}
        title={stateLabel}
        aria-label={`${t("rightSidebar.missionRowAriaLabel", { goal: node.goal })} — ${stateLabel}`}
        className={cn(
          "flex w-full items-start gap-2 px-3 py-2 text-left text-xs hover:bg-cream-50 dark:hover:bg-coffee-900/60",
          // visual emphasis for awaiting-user
          node.awaitingUser && "bg-amber-50/50 dark:bg-amber-950/15",
        )}
      >
        {hasLeaves ? (
          <span
            data-testid={`mission-row-expand-${node.missionId}`}
            role="button"
            tabIndex={0}
            aria-expanded={expanded}
            aria-label={
              expanded
                ? t("rightSidebar.collapseLeavesAriaLabel", { goal: node.goal })
                : t("rightSidebar.expandLeavesAriaLabel", { goal: node.goal })
            }
            onClick={toggleExpanded}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                toggleExpanded(e);
              }
            }}
            className="flex h-4 w-4 shrink-0 items-center justify-center rounded text-slate-400 hover:bg-cream-100 hover:text-slate-700 dark:hover:bg-coffee-800 dark:hover:text-slate-200"
          >
            {expanded ? (
              <ChevronDown size={12} aria-hidden />
            ) : (
              <ChevronRight size={12} aria-hidden />
            )}
          </span>
        ) : (
          <span className="inline-block w-3 shrink-0" aria-hidden />
        )}
        <MissionStateGlyph state={node.state} canceling={node.isCanceling} />
        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-1.5">
            {/* The goal IS the row's content — clamp to 2 lines rather
                than hard-truncating it into uselessness. */}
            <span
              className="line-clamp-2 min-w-0 flex-1 text-[11px] font-normal leading-snug text-slate-500 dark:text-slate-400"
              title={node.goal}
            >
              {node.goal}
            </span>
            {node.queuePosition !== undefined && (
              <span
                data-testid={`mission-row-queue-${node.missionId}`}
                className="shrink-0 rounded bg-cream-200/70 px-1 py-0.5 text-[10px] uppercase tracking-wide text-slate-600 dark:bg-coffee-700/50 dark:text-slate-300"
                title={t("rightSidebar.queuePosition", { n: node.queuePosition })}
              >
                #{node.queuePosition}
              </span>
            )}
          </div>
          {node.awaitingUserTimeout && (
            <div className="mt-0.5 text-[10px] text-amber-600 dark:text-amber-400">
              {t("rightSidebar.awaitingUserTimeout")}
            </div>
          )}
          {/* Badge rides the meta line so the goal keeps the full width. */}
          {(meta || node.origin === "scheduled" || node.caseId) && (
            <div className="mt-0.5 flex flex-wrap items-center gap-1">
              {node.caseId && (
                <CaseChip
                  testId={`mission-row-case-${node.missionId}`}
                  caseId={node.caseId}
                  label={caseLabel}
                  isActiveCase={node.caseId === activeCaseId}
                  registryLoaded={caseRegistryLoaded}
                />
              )}
              {node.origin === "scheduled" && (
                <ScheduledBadge testId={`mission-row-scheduled-${node.missionId}`} />
              )}
              {meta && (
                <span
                  data-testid={`mission-row-meta-${node.missionId}`}
                  className="text-[10px] font-light tabular-nums tracking-wide text-slate-400 dark:text-slate-500"
                >
                  {meta}
                </span>
              )}
            </div>
          )}
        </div>
        {showCancel && (
          <span
            data-testid={`mission-row-cancel-${node.missionId}`}
            role="button"
            tabIndex={0}
            aria-label={t("rightSidebar.cancelAriaLabel", { goal: node.goal })}
            title={t("rightSidebar.cancelAriaLabel", { goal: node.goal })}
            onClick={handleCancel}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                void handleCancel(e as unknown as React.MouseEvent);
              }
            }}
            className="flex h-5 w-5 items-center justify-center rounded text-slate-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/40 dark:hover:text-red-400"
          >
            <Trash2 size={12} />
          </span>
        )}
      </button>
      {expanded && hasLeaves && (
        <ul className="bg-cream-50/40 pl-8 dark:bg-coffee-950/30">
          {node.leaves.map((leaf) => (
            <MissionLeafRow key={leaf.nodeId} leaf={leaf} />
          ))}
        </ul>
      )}
    </li>
  );
}

function MissionStateGlyph({
  state,
  canceling,
}: {
  state: MissionState;
  canceling: boolean;
}) {
  if (canceling) return <StatusBullet status="canceled" />;
  switch (state) {
    // Queued is the one state that is WAITING ON A SLOT rather than resting
    // or working — a clock says that outright, where the grey dot it
    // replaces was indistinguishable from any other not-started state.
    case "queued":
      return (
        <Clock
          size={11}
          className="mt-0.5 shrink-0 text-yellow-500"
          aria-hidden
        />
      );
    case "submitted":
    case "planning":
    case "running":
    case "synthesizing":
      return <StatusBullet status="processing" />;
    case "awaiting_user":
      return <StatusBullet status="awaiting" />;
    case "completed":
      return <StatusBullet status="done" />;
    case "failed":
      return <StatusBullet status="failed" />;
    case "canceled":
      return <StatusBullet status="canceled" />;
    default:
      return <StatusBullet status="pending" />;
  }
}

/** One STEP of a run — the checklist child the operator asked for.
 *
 *  Three stacked pieces of information, in priority order:
 *    1. glyph + the lawyer-facing `label` (never the raw slug when a
 *       summary exists),
 *    2. while running, the live `message`/`step` + `pct` — this is the
 *       "what is it doing RIGHT NOW" line that was entirely missing,
 *    3. on terminal, the step's own duration; on failure, the error.
 *
 *  The label is allowed to wrap to two lines: truncating it to a single
 *  ellipsised line was a large part of why the pane read as opaque. */
function MissionLeafRow({ leaf }: { leaf: MissionLeafNode }) {
  const { t, i18n } = useTranslation();
  const isRunning = leaf.status === "dispatched";
  const isFailed = leaf.status === "failed";
  const showError = isFailed && leaf.error && leaf.error.code !== "user_cancelled";

  // Live line: prefer the human `message`, fall back to the coarser
  // `step` token. `pct` renders as a trailing percentage when present.
  const liveText = leaf.progress?.message ?? leaf.progress?.step;
  const pct = normalizeProgressPct(leaf.progress?.pct) ?? undefined;

  // Live elapsed while the step runs; frozen duration once terminal.
  const now = useNow(isRunning);
  const elapsedMs =
    leaf.durationMs ??
    (leaf.completedAt !== undefined && leaf.startedAt !== undefined
      ? leaf.completedAt - leaf.startedAt
      : isRunning && leaf.startedAt !== undefined
        ? now - leaf.startedAt
        : undefined);

  return (
    <li
      data-testid={`mission-leaf-${leaf.nodeId}`}
      data-status={leaf.status}
      title={`${leaf.label} — ${t(`rightSidebar.leafStatus.${leaf.status}`)}`}
      className="flex items-start gap-2 border-l border-cream-200/40 py-1 pl-3 pr-3 text-[11px] dark:border-coffee-800/40"
    >
      <LeafStatusGlyph status={leaf.status} />
      <div className="min-w-0 flex-1">
        <div className="flex items-start gap-1.5">
          <span
            className={cn(
              "line-clamp-2 min-w-0 flex-1 font-light leading-snug",
              leaf.status === "completed"
                ? "text-slate-500 dark:text-slate-500"
                : "text-slate-700 dark:text-slate-300",
            )}
          >
            {leaf.label}
          </span>
          {elapsedMs !== undefined && (
            <span
              data-testid={`mission-step-elapsed-${leaf.nodeId}`}
              className="shrink-0 text-[10px] font-light tabular-nums text-slate-400 dark:text-slate-500"
            >
              {formatElapsed(elapsedMs)}
            </span>
          )}
        </div>
        {isRunning && (liveText || pct !== undefined) && (
          <div
            data-testid={`mission-step-progress-${leaf.nodeId}`}
            className="mt-0.5 flex items-center gap-1.5 text-[10px] text-blue-600/90 dark:text-blue-400/90"
          >
            {liveText && <span className="min-w-0 truncate">{liveText}</span>}
            {pct !== undefined && (
              <span className="shrink-0 tabular-nums">{pct}%</span>
            )}
          </div>
        )}
        {showError && leaf.error && (
          <div
            data-testid={`mission-step-error-${leaf.nodeId}`}
            className="mt-0.5 text-[10px] leading-snug text-red-500/90 dark:text-red-400/90"
            title={`${leaf.error.code}: ${leaf.error.message}`}
          >
            {leaf.error.message}
          </div>
        )}
      </div>
    </li>
  );
}

/** Sub-item glyphs are deliberately a DIFFERENT FAMILY from the parent
 *  row's: the run header uses filled/circled marks (`CheckCircle2`,
 *  `XCircle`), each step uses the bare stroke equivalents (`Check`,
 *  `X`, `Circle`). Same colour language, lighter weight — so the eye
 *  reads "run" vs "step" from the icon alone instead of having to infer
 *  the hierarchy from indentation. */
function LeafStatusGlyph({ status }: { status: NodeStatus }) {
  const cls = "mt-0.5 shrink-0";
  switch (status) {
    case "awaiting_user":
      return <Pause size={11} className={cn(cls, "text-amber-500")} aria-hidden />;
    case "dispatched":
      return <Loader2 size={11} className={cn(cls, "animate-spin text-blue-500")} aria-hidden />;
    case "pending":
      // Hollow circle = "not started", quieter than a clock face.
      return <Circle size={10} className={cn(cls, "text-slate-300 dark:text-slate-600")} aria-hidden />;
    case "completed":
      return <Check size={11} className={cn(cls, "text-emerald-600/60 dark:text-emerald-500/60")} aria-hidden />;
    case "failed":
      return <X size={11} className={cn(cls, "text-red-500")} aria-hidden />;
    default:
      return <Circle size={10} className={cn(cls, "text-slate-300 dark:text-slate-600")} aria-hidden />;
  }
}
